/**
 * @file     startOAuthSignIn.ts
 * @purpose  Browser entry for "Continue with Google / Apple": remembers the page
 *           the person started on (for lead attribution after the return leg)
 *           and hands off to Supabase signInWithOAuth, returning through
 *           /auth/finish. On success Supabase has already started the redirect
 *           and the result carries no error; the caller keeps its pending state.
 */
import { getSupabaseBrowserClient } from '@/core/supabase/client';
import { oauthRedirectTo, type OAuthProvider } from './oauthProviders';

export const OAUTH_SOURCE_KEY = 'relta.oauth.source_page';

export async function startOAuthSignIn(
  provider: OAuthProvider,
  next: string | null | undefined,
): Promise<{ error: string | null }> {
  try {
    sessionStorage.setItem(OAUTH_SOURCE_KEY, window.location.pathname + window.location.search);
  } catch { /* sessionStorage blocked: attribution falls back to /auth/finish */ }

  const supabase = await getSupabaseBrowserClient();
  if (!supabase) return { error: 'unavailable' };
  const { error } = await supabase.auth.signInWithOAuth({
    provider,
    options: { redirectTo: oauthRedirectTo(window.location.origin, provider, next) },
  });
  return { error: error ? error.message || 'unavailable' : null };
}

export function takeOAuthSourcePage(): string | null {
  let value: string | null = null;
  try {
    value = sessionStorage.getItem(OAUTH_SOURCE_KEY);
    sessionStorage.removeItem(OAUTH_SOURCE_KEY);
  } catch { /* sessionStorage blocked: no start page to report */ }
  return value;
}
